import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useDecorationCatalogQuery } from "../api/useDecorationCatalogQuery";
import {
  defaultEstimatedSizeId,
  defaultVariantId,
  getDecoratingMethod,
  getDecoratingMethodByLabel,
  getPrintCategory,
  getPrintVariant,
} from "../shared/design-catalog.helpers";
import type { DecorationCatalog } from "../shared/design-catalog.types";

type DesignCatalogContextValue = {
  catalog: DecorationCatalog | undefined;
  isLoading: boolean;
  isError: boolean;
  getPrintCategory: (categoryId: string) => ReturnType<typeof getPrintCategory>;
  getPrintVariant: (
    categoryId: string,
    variantId: string,
  ) => ReturnType<typeof getPrintVariant>;
  defaultVariantId: (categoryId: string) => string;
  defaultEstimatedSizeId: typeof defaultEstimatedSizeId;
  getDecoratingMethod: (methodKey: string) => ReturnType<typeof getDecoratingMethod>;
  getDecoratingMethodByLabel: (
    label: string,
  ) => ReturnType<typeof getDecoratingMethodByLabel>;
  defaults: DecorationCatalog["defaults"] | undefined;
};

const DesignCatalogContext = createContext<DesignCatalogContextValue | null>(null);

export function DesignCatalogProvider({ children }: { children: ReactNode }) {
  const { data: catalog, isLoading, isError } = useDecorationCatalogQuery();

  const value = useMemo<DesignCatalogContextValue>(
    () => ({
      catalog,
      isLoading,
      isError,
      getPrintCategory: (categoryId) => getPrintCategory(catalog, categoryId),
      getPrintVariant: (categoryId, variantId) =>
        getPrintVariant(catalog, categoryId, variantId),
      defaultVariantId: (categoryId) => defaultVariantId(catalog, categoryId),
      defaultEstimatedSizeId,
      getDecoratingMethod: (methodKey) => getDecoratingMethod(catalog, methodKey),
      getDecoratingMethodByLabel: (label) =>
        getDecoratingMethodByLabel(catalog, label),
      defaults: catalog?.defaults,
    }),
    [catalog, isError, isLoading],
  );

  return (
    <DesignCatalogContext.Provider value={value}>
      {children}
    </DesignCatalogContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- Hook co-located with context provider
export function useDesignCatalog(): DesignCatalogContextValue {
  const ctx = useContext(DesignCatalogContext);
  if (!ctx) {
    throw new Error("useDesignCatalog must be used within DesignCatalogProvider");
  }
  return ctx;
}

/** Optional hook for surfaces outside the provider. */
// eslint-disable-next-line react-refresh/only-export-components -- Hook co-located with context provider
export function useOptionalDesignCatalog(): DesignCatalogContextValue | null {
  return useContext(DesignCatalogContext);
}
